import axios from "axios"
import SnackbarComponent from "components/SnackBar"
import config from "config/config"
import { formatDate } from "helpers/date-format"
import React, { useEffect, useState } from "react"
import { setBreadcrumbItems } from "../../store/actions"
import { connect, useSelector } from "react-redux"
import {
  Table,
  Pagination,
  PaginationItem,
  PaginationLink,
  Input,
  FormGroup,
  Label,
  Button,
  Card,
  CardBody,
  CardHeader,
  Row,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap"
import { IconButton } from "@mui/material"
import FilterCard from "./FilterCard"

const CreateAudience = props => {
  const [currentPage, setCurrentPage] = useState(1)
  const [customers, setCustomers] = useState([])
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [audienceName, setAudienceName] = useState("")
  const [audienceDesc, setAudienceDesc] = useState("")
  const [selectedSurvey, setSelectedSurvey] = useState("")
  const [nonRespondersOnly, setNonRespondersOnly] = useState(false)
  const [negativeFeedbackOnly, setNegativeFeedbackOnly] = useState(false)
  const mode = useSelector(state => state.Layout.layoutMode)
  const [success, setSuccess] = useState(null)
  const [
    surveyCustomerAssignmentsForAudience,
    setSurveyCustomerAssignmentsForAudience,
  ] = useState(null)
  const [selectedCustomers, setSelectedCustomers] = useState(new Set())
  const [selectAll, setSelectAll] = useState(false)
  const [fromDate, setFromDate] = useState("")
  const [toDate, setToDate] = useState("")
  const customersPerPage = 10

  const activeOrganization = useSelector(
    state => state.organizationReducer.activeOrganization,
  )
  const leftSideBarType = useSelector(state => state.Layout.leftSideBarType)
  const sidebarWidth = leftSideBarType === "condensed" ? 85 : 260
  document.title = "Create Audience | Reputation Management"

  const breadcrumbItems = [
    { title: `${activeOrganization.name}`, link: "#" },
    { title: "Surveys", link: "/survey-dashboard" },
    { title: "Create Audience", link: "#" },
  ]

  const userData = useSelector(state => state.Login.userData)

  const indexOfLastCustomer = currentPage * customersPerPage
  const indexOfFirstCustomer = indexOfLastCustomer - customersPerPage

  const isWithinDateRange = visitDate => {
    if (!fromDate && !toDate) return true
    const visitTime = new Date(visitDate).getTime()
    const fromTime = fromDate ? new Date(fromDate).getTime() : -Infinity
    const toTime = toDate ? new Date(toDate).getTime() : Infinity
    return visitTime >= fromTime && visitTime <= toTime
  }

  const filteredCustomers = customers.filter(customer => {
    const assignments = surveyCustomerAssignmentsForAudience?.filter(
      assignment => assignment.customerId === customer.id,
    )

    // ✅ Ensure we only check for the selected survey
    const selectedSurveyAssignments = assignments?.filter(
      a => a.surveyId === parseInt(selectedSurvey),
    )

    if (selectedSurvey && selectedSurveyAssignments.length === 0) {
      return false // Skip customers who are not part of the selected survey
    }

    if (
      nonRespondersOnly &&
      selectedSurveyAssignments.every(a => a.Responded)
    ) {
      return false // Skip customers who have responded in the selected survey
    }

    if (
      negativeFeedbackOnly &&
      selectedSurveyAssignments.every(
        a => a.sentimentScore === null || a.sentimentScore >= 5,
      )
    ) {
      return false // Skip customers who do not have negative feedback
    }

    return true
  })

  const currentCustomers = filteredCustomers.slice(
    indexOfFirstCustomer,
    indexOfLastCustomer,
  )

  const totalPages = Math.ceil(filteredCustomers.length / customersPerPage)

  const fetchSurveyCustomerAssignmentsForAudience = async () => {
    try {
      const response = await axios.get(
        `${config.backendUrl}/survey/getSurveyCustomerAssignmentsForAudience/${activeOrganization.id}`,
        {
          headers: {
            Authorization: `Bearer ${userData.token}`,
          },
        },
      )

      setSurveyCustomerAssignmentsForAudience(response.data.assignments)
    } catch (err) {
      console.log(err)
      setErr("Error fetching Survey Customer Assignments For Audience.")
    }
  }

  const fetchCustomers = async () => {
    try {
      setLoading(true)
      const response = await axios.get(
        `${config.backendUrl}/customers/${activeOrganization.id}`,
        {
          headers: {
            Authorization: `Bearer ${userData.token}`,
          },
        },
      )

      setCustomers(response.data.customers)
      setLoading(false)
    } catch (err) {
      setLoading(false)
      setErr("Error fetching customers.")
    }
  }

  useEffect(() => {
    fetchCustomers()
    fetchSurveyCustomerAssignmentsForAudience()
    props.setBreadcrumbItems(
      `${activeOrganization.name} (${activeOrganization.type})`,
      breadcrumbItems,
    )
  }, [activeOrganization])

  // ✅ Toggle Selection
  const toggleSelection = customerId => {
    setSelectedCustomers(prevSelected => {
      const newSelected = new Set(prevSelected)
      if (newSelected.has(customerId)) {
        newSelected.delete(customerId)
      } else {
        newSelected.add(customerId)
      }
      return newSelected
    })
  }

  // ✅ Select/Unselect All Customers
  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedCustomers(new Set()) // Unselect all
    } else {
      setSelectedCustomers(
        new Set(filteredCustomers.map(customer => customer.id)),
      ) // Select all filtered
    }
    setSelectAll(!selectAll)
  }

  const handleCreateAudienceClick = () => {
    if (selectedCustomers.size > 2) {
      setModalOpen(true)
    } else {
      setErr("Please select at least 3 customers.")
    }
  }

  const finalizeCreateAudience = async () => {
    try {
      const audienceDataToSend = {
        name: audienceName,
        description: audienceDesc,
        customers: Array.from(selectedCustomers),
      }
      const response = await axios.post(
        `${config.backendUrl}/audience/createAudience/${activeOrganization.id}`,
        { audience: audienceDataToSend },
        {
          headers: {
            Authorization: `Bearer ${userData.token}`,
          },
        },
      )
      setSuccess("Audience created successfully!")
      setModalOpen(false)
      setSelectedCustomers(new Set())
      setSelectAll(false)
    } catch (err) {
      setErr("Error while creating audience.")
    }
  }

  return (
    <div>
      <SnackbarComponent
        err={err}
        setErr={setErr}
        success={success}
        setSuccess={setSuccess}
      />

      {/* ✅ Filter Card (Light Themed Header) */}
      <FilterCard
        fromDate={fromDate}
        setFromDate={setFromDate}
        toDate={toDate}
        setToDate={setToDate}
        selectAll={selectAll}
        toggleSelectAll={toggleSelectAll}
        selectedSurvey={selectedSurvey}
        setSelectedSurvey={setSelectedSurvey}
        nonRespondersOnly={nonRespondersOnly}
        setNonRespondersOnly={setNonRespondersOnly}
        negativeFeedbackOnly={negativeFeedbackOnly}
        setNegativeFeedbackOnly={setNegativeFeedbackOnly}
        surveyAssignments={surveyCustomerAssignmentsForAudience}
      />
      <Card
        className="mb-4 shadow-sm br"
        style={{
          backgroundColor: mode === "dark" ? "#252F40" : "#fff",
        }}
      >
        <CardHeader
          style={{
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            backgroundColor: mode === "dark" ? "#252F40" : "#fff",
          }}
        >
          <h5 className="mb-0">Customers</h5>
        </CardHeader>
        <CardBody>
          <Table striped>
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    className="app-checkbox"
                    checked={
                      selectedCustomers.size === filteredCustomers.length &&
                      filteredCustomers.length > 0
                    }
                    onChange={toggleSelectAll}
                  />
                </th>
                <th>#</th>
                <th>First Name</th>
                <th>Last Name</th>
                <th>Email</th>
                <th>Contact</th>
                <th>Visit Dates</th>
              </tr>
            </thead>
            <tbody>
              {currentCustomers.map((customer, index) => (
                <tr key={customer.id}>
                  <td>
                    <input
                      type="checkbox"
                      className="app-checkbox"
                      checked={selectedCustomers.has(customer.id)}
                      onChange={() => toggleSelection(customer.id)}
                    />
                  </td>
                  <td>{indexOfFirstCustomer + index + 1}</td>
                  <td>{customer.firstName}</td>
                  <td>{customer.lastName}</td>
                  <td>{customer.email}</td>
                  <td>{customer.contact}</td>
                  <td>
                    {customer.CustomerVisits.length > 0
                      ? customer.CustomerVisits.filter(visit =>
                          isWithinDateRange(visit.visitDate),
                        )
                          .map(visit => formatDate(visit.visitDate))
                          .join(", ")
                      : "No visits"}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {/* ✅ Pagination Controls */}
          <Pagination className="mt-3 justify-content-center">
            {/* Previous Button */}
            <PaginationItem disabled={currentPage === 1}>
              <PaginationLink
                style={{
                  color: mode === "dark" ? "#fff" : "#000",
                  backgroundColor: "transparent",
                  borderColor: mode === "dark" ? "#444" : "#dee2e6",
                }}
                previous
                onClick={() => setCurrentPage(currentPage - 1)}
              />
            </PaginationItem>

            {/* Page Numbers */}
            {Array.from({ length: totalPages }).map((_, i) => (
              <PaginationItem active={i + 1 === currentPage} key={i}>
                <PaginationLink
                  style={{
                    borderColor:
                      i + 1 === currentPage
                        ? "#2990DF"
                        : mode === "dark"
                          ? "#444"
                          : "#dee2e6",
                    boxShadow: "none",
                    backgroundColor:
                      i + 1 === currentPage ? "#2990DF" : "transparent",
                    color:
                      i + 1 === currentPage
                        ? "#fff"
                        : mode === "dark"
                          ? "#fff"
                          : "#000",
                  }}
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </PaginationLink>
              </PaginationItem>
            ))}

            {/* Next Button */}
            <PaginationItem disabled={currentPage === totalPages}>
              <PaginationLink
                style={{
                  color: mode === "dark" ? "#fff" : "#000",
                  backgroundColor: "transparent",
                  borderColor: mode === "dark" ? "#444" : "#dee2e6",
                }}
                next
                onClick={() => setCurrentPage(currentPage + 1)}
              />
            </PaginationItem>
          </Pagination>
        </CardBody>
      </Card>

      {selectedCustomers.size > 0 && (
        <div
          style={{
            position: "fixed",
            bottom: "32px",
            left: `calc(${sidebarWidth}px + (100vw - ${sidebarWidth}px) / 2)`,
            transform: "translateX(-50%)",
            zIndex: 1050,
            backgroundColor: mode === "dark" ? "#1a2332" : "#fff",
            border: `1px solid ${mode === "dark" ? "#3a4556" : "#e5e7eb"}`,
            borderRadius: "14px",
            boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
            padding: "14px 24px",
            display: "flex",
            alignItems: "center",
            gap: "20px",
            minWidth: "340px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px", flex: 1 }}>
            <div
              style={{
                width: "36px",
                height: "36px",
                borderRadius: "50%",
                backgroundColor: "#EBF5FF",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                color: "#2990DF",
                fontSize: "14px",
              }}
            >
              {selectedCustomers.size}
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: "14px", color: mode === "dark" ? "#fff" : "#111827" }}>
                {selectedCustomers.size} customer{selectedCustomers.size !== 1 ? "s" : ""} selected
              </div>
              <div style={{ fontSize: "12px", color: mode === "dark" ? "#9ca3af" : "#6b7280" }}>
                Ready to create audience
              </div>
            </div>
          </div>
          <button
            onClick={() => setSelectedCustomers(new Set())}
            style={{
              background: "transparent",
              border: `1px solid ${mode === "dark" ? "#3a4556" : "#e5e7eb"}`,
              borderRadius: "8px",
              padding: "8px 14px",
              fontSize: "13px",
              color: mode === "dark" ? "#9ca3af" : "#6b7280",
              cursor: "pointer",
              fontFamily: "Inter, sans-serif",
            }}
          >
            Clear
          </button>
          <button
            onClick={handleCreateAudienceClick}
            style={{
              backgroundColor: "#2990DF",
              border: "none",
              borderRadius: "8px",
              padding: "10px 22px",
              fontSize: "14px",
              fontWeight: 600,
              color: "#fff",
              cursor: "pointer",
              fontFamily: "Inter, sans-serif",
              whiteSpace: "nowrap",
            }}
          >
            Create Audience
          </button>
        </div>
      )}
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(!modalOpen)} centered>
        <div
          style={{
            backgroundColor: mode === "dark" ? "#1f2937" : "#fff",
            borderRadius: "16px",
            overflow: "hidden",
            fontFamily: "Inter, sans-serif",
          }}
        >
          {/* Header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "20px 24px 16px",
              borderBottom: `1px solid ${mode === "dark" ? "#374151" : "#e5e7eb"}`,
            }}
          >
            <div>
              <h5 style={{ margin: 0, fontWeight: 700, fontSize: "17px", color: mode === "dark" ? "#fff" : "#111827" }}>
                Create Audience
              </h5>
              <p style={{ margin: 0, fontSize: "13px", color: mode === "dark" ? "#9ca3af" : "#6b7280", marginTop: "2px" }}>
                {selectedCustomers.size} customer{selectedCustomers.size !== 1 ? "s" : ""} will be added
              </p>
            </div>
            <button
              onClick={() => setModalOpen(false)}
              style={{
                background: "transparent",
                border: "none",
                cursor: "pointer",
                color: mode === "dark" ? "#9ca3af" : "#6b7280",
                fontSize: "20px",
                lineHeight: 1,
                padding: "4px",
              }}
            >
              ×
            </button>
          </div>

          {/* Body */}
          <div style={{ padding: "24px" }}>
            <div style={{ marginBottom: "18px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "12px",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  color: mode === "dark" ? "#9ca3af" : "#6b7280",
                  marginBottom: "6px",
                  letterSpacing: "0.05em",
                }}
              >
                Audience Name
              </label>
              <input
                type="text"
                value={audienceName}
                onChange={e => setAudienceName(e.target.value)}
                placeholder="e.g. Non-responders Q1 2025"
                style={{
                  width: "100%",
                  padding: "10px 14px",
                  borderRadius: "8px",
                  border: `1px solid ${mode === "dark" ? "#374151" : "#e5e7eb"}`,
                  backgroundColor: mode === "dark" ? "#374151" : "#f9fafb",
                  color: mode === "dark" ? "#fff" : "#111827",
                  fontSize: "14px",
                  fontFamily: "Inter, sans-serif",
                  outline: "none",
                }}
              />
            </div>

            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "12px",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  color: mode === "dark" ? "#9ca3af" : "#6b7280",
                  marginBottom: "6px",
                  letterSpacing: "0.05em",
                }}
              >
                Description
              </label>
              <textarea
                value={audienceDesc}
                onChange={e => setAudienceDesc(e.target.value)}
                placeholder="Brief description of this audience..."
                rows={3}
                style={{
                  width: "100%",
                  padding: "10px 14px",
                  borderRadius: "8px",
                  border: `1px solid ${mode === "dark" ? "#374151" : "#e5e7eb"}`,
                  backgroundColor: mode === "dark" ? "#374151" : "#f9fafb",
                  color: mode === "dark" ? "#fff" : "#111827",
                  fontSize: "14px",
                  fontFamily: "Inter, sans-serif",
                  outline: "none",
                  resize: "vertical",
                }}
              />
            </div>
          </div>

          {/* Footer */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "10px",
              padding: "16px 24px 20px",
              borderTop: `1px solid ${mode === "dark" ? "#374151" : "#e5e7eb"}`,
            }}
          >
            <button
              onClick={() => setModalOpen(false)}
              style={{
                padding: "10px 20px",
                borderRadius: "8px",
                border: `1px solid ${mode === "dark" ? "#374151" : "#e5e7eb"}`,
                backgroundColor: "transparent",
                color: mode === "dark" ? "#9ca3af" : "#6b7280",
                fontSize: "14px",
                fontWeight: 500,
                cursor: "pointer",
                fontFamily: "Inter, sans-serif",
              }}
            >
              Cancel
            </button>
            <button
              onClick={finalizeCreateAudience}
              style={{
                padding: "10px 24px",
                borderRadius: "8px",
                border: "none",
                backgroundColor: "#2990DF",
                color: "#fff",
                fontSize: "14px",
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "Inter, sans-serif",
              }}
            >
              Create Audience
            </button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default connect(null, { setBreadcrumbItems })(CreateAudience)
